import React, { type PropsWithChildren } from 'react';
import {
  MasonryEngineFormFieldNode,
  MasonryEngineNodeRendererProps,
} from '@leagueplatform/masonry-engine';
import {
  Button,
  Fieldset,
  GDSFieldsetProps,
  ParagraphText,
  Popover,
} from '@leagueplatform/genesis-core';

export type MasonryFieldWrapperProps = PropsWithChildren<
  Pick<
    MasonryEngineNodeRendererProps<MasonryEngineFormFieldNode>,
    'formFieldProperties' | 'rootElementRef' | 'className' | 'locatorId' | 'css'
  > & {
    disabled?: boolean;
  } & Omit<GDSFieldsetProps, 'children' | 'tooltip' | 'legend'>
>;

/**
 * Renders a fieldset within a masonry layout with optional tooltip and status message.
 *
 */
export const MasonryFieldsetWrapper = ({
  rootElementRef,
  formFieldProperties,
  disabled,
  children,
  css,
  onChange,
  className,
  locatorId,
}: MasonryFieldWrapperProps) => {
  const {
    formFieldId,
    inputStatus,
    statusMessage,
    label,
    hideLabel,
    tooltipIcon,
    tooltipContent,
    tooltipLabel,
    hint,
    showOptionalFlag,
  } = formFieldProperties;
  return (
    <Fieldset
      ref={rootElementRef}
      id={formFieldId}
      name={formFieldId}
      legend={label}
      hideLegend={hideLabel}
      hint={hint}
      required={!showOptionalFlag}
      inputStatus={inputStatus}
      statusMessage={statusMessage}
      disabled={disabled}
      onChange={onChange}
      css={css}
      tooltip={
        tooltipContent &&
        tooltipIcon && (
          <Popover.Root>
            <Popover.Trigger>
              <Button
                icon={tooltipIcon}
                quiet
                hideLabel
                priority="secondary"
                size="small"
                css={{ marginInlineStart: '0.5rem' }}
              >
                {tooltipLabel}
              </Button>
            </Popover.Trigger>
            <Popover.Content align="start" side="top" showCloseButton={false}>
              <ParagraphText size="sm">{tooltipContent}</ParagraphText>
            </Popover.Content>
          </Popover.Root>
        )
      }
      className={className}
      data-testid={locatorId}
    >
      <div className="GDS-input-wrapper">{children}</div>
    </Fieldset>
  );
};
